import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const team = [
  { name: '林致远', role: '总编辑', desc: '从事新闻采编工作十余年，负责内容的整体把关与选题方向。', icon: 'ri-user-star-line' },
  { name: '苏晓桐', role: '娱乐频道主编', desc: '专注影视与文娱行业报道，擅长挖掘话题背后的行业逻辑。', icon: 'ri-movie-2-line' },
  { name: '周子墨', role: '生活频道主笔', desc: '关注健康、家居与日常实用技巧，坚持内容的实用与可操作性。', icon: 'ri-leaf-line' },
  { name: '陈嘉禾', role: '产品与技术负责人', desc: '负责平台技术架构与体验优化，致力于让阅读更流畅舒适。', icon: 'ri-code-s-slash-line' },
];

const timeline = [
  { year: '2021', title: '平台起步', desc: '9·1牛网正式上线，最初以新闻资讯聚合为主，服务第一批用户。' },
  { year: '2023', title: '频道扩展', desc: '陆续开设娱乐频道与生活频道，内容版图从时事扩展至多元领域。' },
  { year: '2025', title: '体验升级', desc: '全面优化搜索、筛选与推荐能力，推出多端适配的全新阅读体验。' },
  { year: '2026', title: '生态共建', desc: '持续完善内容生态，探索更丰富的内容形态与创作者合作方式。' },
];

const scope = [
  { icon: 'ri-newspaper-line', title: '新闻资讯', desc: '时政、经济、民生与社会热点的及时报道与深度梳理。' },
  { icon: 'ri-movie-2-line', title: '娱乐文娱', desc: '影视、音乐、综艺与网络视听的最新动态与话题讨论。' },
  { icon: 'ri-leaf-line', title: '生活服务', desc: '健康、家居、出行与实用技巧，帮助用户提升生活品质。' },
  { icon: 'ri-search-eye-line', title: '内容检索', desc: '关键词搜索、分类筛选与相关推荐，让内容触手可及。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 9·1牛网',
    description:
      '9·1牛网是一个综合资讯平台，面向广泛的大陆华语用户群体，特别是关注新闻时事、娱乐动态及生活实用信息的成年人。我们致力于提供及时、丰富且多元的资讯内容，帮助用户高效获取有价值的信息。',
  });
  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="mt-4 rounded-lg overflow-hidden bg-primary-800 relative">
          <div className="absolute inset-0 opacity-25">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20background%20with%20overlapping%20translucent%20geometric%20panels%20in%20deep%20navy%20blue%20and%20warm%20orange%20accents%2C%20modern%20minimal%20magazine%20texture%2C%20clean%20elegant%20composition%2C%20no%20text&width=1600&height=560&seq=about-hero-91niu&orientation=landscape"
              alt="9·1牛网简介背景图"
              title="关于我们 - 9·1牛网"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 p-6 md:p-12">
            <h1 className="font-heading font-black text-2xl md:text-4xl text-background-50">关于 9·1牛网</h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base text-primary-100/90 leading-relaxed">
              9·1牛网是一个综合资讯平台，面向广泛的大陆华语用户群体，特别是关注新闻时事、娱乐动态及生活实用信息的成年人。我们致力于提供及时、丰富且多元的资讯内容，帮助用户高效获取有价值的信息。
            </p>
          </div>
        </section>

        <section className="mt-10 md:mt-14">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">我们的理念</h2>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: 'ri-checkbox-circle-line', title: '真实为本', desc: '坚持内容核实与来源可溯，让每一条资讯经得起推敲。' },
              { icon: 'ri-layout-masonry-line', title: '清晰易读', desc: '以清晰的层级与舒适的排版，让阅读高效而不费力。' },
              { icon: 'ri-service-line', title: '用户优先', desc: '围绕用户的真实需求打磨功能，持续优化使用体验。' },
            ].map((v) => (
              <div key={v.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${v.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-14">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">团队介绍</h2>
          <p className="mt-2 text-sm text-foreground-500">以下为虚拟示例成员，仅用于展示</p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((m) => (
              <div key={m.name} className="p-5 rounded-lg bg-background-50 border border-background-200 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className={`${m.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{m.name}</h3>
                <p className="text-xs text-secondary-600 mt-1">{m.role}</p>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-14">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">发展历程</h2>
          <div className="mt-6 relative pl-6 border-l-2 border-background-200 space-y-8">
            {timeline.map((t) => (
              <div key={t.year} className="relative">
                <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-accent-500 ring-4 ring-background-50" />
                <p className="font-heading font-black text-lg text-primary-600">{t.year}</p>
                <h3 className="mt-1 font-bold text-base text-foreground-950">{t.title}</h3>
                <p className="mt-1 text-sm text-foreground-600 leading-relaxed max-w-2xl">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-14">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">业务范围</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {scope.map((s) => (
              <div key={s.title} className="flex gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 shrink-0 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className={`${s.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-bold text-base text-foreground-950">{s.title}</h3>
                  <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-14 mb-4 rounded-lg bg-accent-50 border border-accent-200 p-6 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">有想法或建议？</h2>
            <p className="mt-2 text-sm text-foreground-600">欢迎随时联系我们，你的反馈是我们前进的动力。</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            前往联系我们
            <i className="ri-arrow-right-line" />
          </Link>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}